<template>
  <div class="carousel-management">
    <div class="page-breadcrumb">
      <span>內容管理</span> <i class="fas fa-chevron-right separator"></i> <span>首頁輪播圖管理</span>
    </div>

    <div class="actions-bar">
      <button class="add-banner-btn" @click="openModalForCreate">
        <i class="fas fa-plus-circle"></i> 新增輪播圖
      </button>
    </div>

    <div v-if="isLoading" class="loading-spinner">
      <p>載入輪播圖中...</p>
    </div>

    <div v-if="!isLoading && banners.length > 0" class="banner-grid">
      <div v-for="(banner, index) in banners" :key="banner.id || index" class="banner-card">
        <div class="banner-image-container">
          <img :src="banner.imageUrl" :alt="banner.title || '輪播圖 ' + (index + 1)" class="banner-image">
          <span v-if="!banner.enabled" class="status-badge disabled-badge">已停用</span>
        </div>
        <div class="banner-info">
          <h4 class="banner-title">{{ banner.title || '未命名輪播圖' }}</h4>
          <p class="banner-detail">順序: {{ banner.sortOrder === null || banner.sortOrder === undefined ? '未設定' : banner.sortOrder }}</p>
          <p v-if="banner.linkUrl" class="banner-link">
            連結: <a :href="banner.linkUrl" target="_blank" rel="noopener noreferrer">{{ banner.linkUrl }}</a>
          </p>
          <p v-else class="banner-link">無連結</p>
        </div>
        <div class="banner-actions">
          <button class="action-btn edit-btn" @click="openModalForEdit(banner)">
            <i class="fas fa-edit"></i> 編輯
          </button>
          <button class="action-btn delete-btn" @click="confirmDeleteBanner(banner)">
            <i class="fas fa-trash-alt"></i> 刪除
          </button>
        </div>
      </div>
    </div>

    <div v-if="!isLoading && banners.length === 0 && !selectedFile" class="no-data-message">
      目前沒有任何輪播圖片。請點擊上方按鈕上傳。
    </div>

    <div v-if="isModalVisible" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content carousel-modal-content">
        <h4 class="modal-title">{{ modalTitle }}</h4>
        <form @submit.prevent="handleSaveBanner">
          <div class="form-group">
            <label for="bannerTitle">標題 (可選)：</label>
            <input type="text" id="bannerTitle" v-model="currentBanner.title" class="form-input" placeholder="輸入輪播圖標題">
          </div>
          <div class="form-group">
            <label for="bannerLinkUrl">連結網址 (可選)：</label>
            <input type="url" id="bannerLinkUrl" v-model="currentBanner.linkUrl" class="form-input" placeholder="例如：https://example.com/page">
          </div>
          <div class="form-group">
            <label for="bannerSortOrder">顯示順序 (數字越小越前面)：</label>
            <input type="number" id="bannerSortOrder" v-model.number="currentBanner.sortOrder" class="form-input" placeholder="輸入數字，例如：0">
          </div>
          <div class="form-group">
            <label for="bannerImageUpload">輪播圖片：<span v-if="!isEditingModal && !currentBanner.imageUrl" class="required-star">*</span></label>
            <input type="file" id="bannerImageUpload" @change="handleImageFileChange" accept="image/png, image/jpeg, image/webp" class="form-input-file">
            <div v-if="imagePreviewUrl || currentBanner.imageUrl" class="image-preview-container-modal">
              <img :src="imagePreviewUrl || currentBanner.imageUrl" alt="圖片預覽" class="image-preview-modal">
              <button type="button" @click="removeSelectedImage" class="remove-image-btn" v-if="selectedImageFile || (isEditingModal && currentBanner.imageUrl)">
                {{ isEditingModal && currentBanner.imageUrl && !selectedImageFile ? '移除現有圖片' : '清除選擇' }}
              </button>
            </div>
            <small v-if="!imagePreviewUrl && !currentBanner.imageUrl" class="image-upload-hint">
              (建議寬高比 16:7 或類似，500KB 以下的 PNG/JPG/WEBP)
            </small>
          </div>
          <div class="form-group toggle-form-group">
            <label for="bannerEnabled" class="toggle-switch-label">是否啟用：</label>
            <label class="switch">
              <input type="checkbox" id="bannerEnabled" v-model="currentBanner.enabled">
              <span class="slider round"></span>
            </label>
          </div>
          <div class="modal-actions">
            <button type="button" @click="closeModal" class="modal-btn cancel-btn">取消</button>
            <button type="submit" class="modal-btn save-btn" :disabled="isSaving">
              <span v-if="isSaving"><i class="fas fa-spinner fa-spin"></i> 儲存中...</span>
              <span v-else>儲存</span>
            </button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import Swal from '@/utils/chineseSwal';
import { uploadBannerImage } from '@/api/file.api.js';
import {
  getAllCarousels,
  createCarousel,
  updateCarousel,
  deleteCarousel
} from '@/api/carousel.api.js';

const toast = useToast();

const banners = ref([]);
const isLoading = ref(true); // 修改：原為 isLoadingBanners
const isSaving = ref(false);

const isModalVisible = ref(false);
const modalTitle = ref('');
const currentBanner = ref({ // 更新 currentBanner 結構以匹配 DTO
  id: null,
  title: '',
  linkUrl: '',
  imageUrl: '',
  sortOrder: 0,   // 給予預設值
  enabled: true,  // 預設為啟用
});
const isEditingModal = ref(false); // 重命名 isEditing -> isEditingModal 以避免與其他潛在變數衝突
const selectedImageFile = ref(null);
const imagePreviewUrl = ref('');
const uploadError = ref('');


const fetchBanners = async () => {
  isLoading.value = true;
  try {
    const data = await getAllCarousels();
    // 確保後端返回的數據包含 DTO 中的所有欄位，並給予預設值
    banners.value = data.map(b => ({
      id: b.id,
      imageUrl: b.imageUrl || '',
      title: b.title || '',
      linkUrl: b.linkUrl || '',
      sortOrder: b.sortOrder === undefined || b.sortOrder === null ? 0 : b.sortOrder,
      enabled: b.enabled === undefined || b.enabled === null ? true : b.enabled,
    })).sort((a,b) => (a.sortOrder || 0) - (b.sortOrder || 0)); // 按 sortOrder 排序
  } catch (error) {
    console.error("獲取輪播圖失敗:", error);
    toast.error('獲取輪播圖資料失敗。');
    banners.value = [];
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchBanners);

const resetCurrentBanner = () => {
  currentBanner.value = {
    id: null, title: '', linkUrl: '', imageUrl: '',
    sortOrder: 0, enabled: true, // 包含新欄位的預設值
  };
  selectedImageFile.value = null;
  imagePreviewUrl.value = '';
  const fileInput = document.getElementById('bannerImageUpload');
  if (fileInput) fileInput.value = '';
};

const openModalForCreate = () => {
  resetCurrentBanner();
  isEditingModal.value = false;
  modalTitle.value = '新增輪播圖';
  isModalVisible.value = true;
};

const openModalForEdit = (banner) => {
  isEditingModal.value = true;
  currentBanner.value = {
    ...banner, // 展開已有的 banner 數據
    sortOrder: banner.sortOrder === undefined || banner.sortOrder === null ? 0 : banner.sortOrder,
    enabled: banner.enabled === undefined || banner.enabled === null ? true : banner.enabled,
  };
  selectedImageFile.value = null;
  imagePreviewUrl.value = '';
  modalTitle.value = '編輯輪播圖';
  isModalVisible.value = true;
};

const closeModal = () => {
  isModalVisible.value = false;
  // resetCurrentBanner(); // 可以在關閉時重置，或保留上次編輯的內容直到下次打開
};

const handleImageFileChange = (event) => {
  const file = event.target.files[0];
  uploadError.value = '';
  if (file) {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    const maxSize = 500 * 1024;
    if (!allowedTypes.includes(file.type)) {
      toast.error('檔案格式錯誤，僅支援 PNG / JPG / WEBP 圖片。');
      event.target.value = ''; selectedImageFile.value = null; imagePreviewUrl.value = ''; return;
    }
    if (file.size > maxSize) {
      toast.error('圖片大小不得超過 500KB。');
      event.target.value = ''; selectedImageFile.value = null; imagePreviewUrl.value = ''; return;
    }
    selectedImageFile.value = file;
    imagePreviewUrl.value = URL.createObjectURL(file);
  } else {
    selectedImageFile.value = null;
    imagePreviewUrl.value = '';
  }
};

const removeSelectedImage = () => {
  selectedImageFile.value = null;
  imagePreviewUrl.value = '';
  if (isEditingModal.value) { // 編輯模式下清除圖片，意味著希望刪除或不更新圖片
    currentBanner.value.imageUrl = ''; // 將 imageUrl 設為空，後端需處理此情況
  }
  const fileInput = document.getElementById('bannerImageUpload');
  if (fileInput) fileInput.value = '';
};

const handleSaveBanner = async () => {
  isSaving.value = true;
  let finalImageUrl = currentBanner.value.imageUrl;

  // 步驟1: 如果有新選擇的圖片，則上傳它
  if (selectedImageFile.value) {
    try {
      const toastId = toast.info('圖片上傳中...', { timeout: false, id: 'banner-img-uploading' });
      const uploadResult = await uploadBannerImage(selectedImageFile.value);
      toast.dismiss(toastId);
      if (uploadResult && uploadResult.url) {
        finalImageUrl = uploadResult.url;
      } else {
        throw new Error(uploadResult.error || '圖片上傳成功但未返回有效的 URL。');
      }
    } catch (error) {
      toast.dismiss('banner-img-uploading');
      console.error("輪播圖片上傳失敗:", error);
      const uploadErrorMessage = error.response?.data?.error || error.message || '圖片上傳操作失敗。';
      toast.error(uploadErrorMessage);
      isSaving.value = false;
      return;
    }
  }

  // 步驟2: 檢查圖片 (新增時必須有圖片)
  if (!isEditingModal.value && !finalImageUrl) {
    toast.warning('新增輪播圖時必須上傳一張圖片。');
    isSaving.value = false;
    return;
  }

  // 步驟3: 準備要提交的數據
  const bannerData = {
    title: currentBanner.value.title,
    linkUrl: currentBanner.value.linkUrl,
    imageUrl: finalImageUrl,
    sortOrder: Number(currentBanner.value.sortOrder) || 0, // 確保是數字
    enabled: !!currentBanner.value.enabled, // 確保是布林值
  };

  try {
    if (isEditingModal.value) {
      await updateCarousel(currentBanner.value.id, bannerData);
      toast.success('輪播圖更新成功！');
    } else {
      await createCarousel(bannerData);
      toast.success('輪播圖新增成功！');
    }
    closeModal();
    await fetchBanners(); // 刷新列表
  } catch (error) {
    console.error("保存輪播圖失敗:", error);
    const errorMessage = error.response?.data?.message || error.message || '操作失敗，請稍後再試。';
    toast.error(`保存輪播圖失敗：${errorMessage}`);
  } finally {
    isSaving.value = false;
  }
};

const confirmDeleteBanner = async (banner) => {
  Swal.fire({
    title: '確定要刪除嗎?',
    html: `您確定要刪除輪播圖 <br><strong>${banner.title || '此圖片'}</strong> 嗎？<br><img src="${banner.imageUrl}" alt="預覽" style="max-width: 100%; max-height: 100px; margin-top: 10px; border-radius: 4px;">`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: '是的，刪除！',
    cancelButtonText: '取消',
    customClass: { /* ... SweetAlert2 dark styles ... */ },
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await deleteCarousel(banner.id);
        toast.success('輪播圖已成功刪除！');
        await fetchBanners();
      } catch (error) {
        console.error("刪除輪播圖失敗:", error);
        const errorMessage = error.response?.data?.message || error.message || '操作失敗，請稍後再試。';
        toast.error(`刪除輪播圖失敗：${errorMessage}`);
      }
    }
  });
};
</script>

<style scoped>
/* carousel-management, page-breadcrumb, actions-bar, add-banner-btn, loading-spinner, no-data-message 與之前類似 */
.carousel-management {
  padding: 20px;
  color: #c0c0c0;
}

.page-breadcrumb {
  font-size: 0.95em;
  color: #a0a0b0;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
}
.page-breadcrumb .separator { margin: 0 8px; font-size: 0.7em; }

.actions-bar {
  margin-bottom: 25px;
  display: flex;
  gap: 15px;
  align-items: center;
}

.add-banner-btn {
  background: linear-gradient(145deg, #50fa7b, #3ddc67); /* 與新增商品按鈕風格一致 */
  color: #1a1a1a;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 8px rgba(80, 250, 123, 0.2);
  display: inline-flex;
  align-items: center;
}
.add-banner-btn i {
  margin-right: 8px;
}
.add-banner-btn:hover {
  background: linear-gradient(145deg, #60fc8b, #4dfd77);
  box-shadow: 0 4px 12px rgba(80, 250, 123, 0.3);
  transform: translateY(-1px);
}

.loading-spinner, .no-data-message {
  text-align: center;
  padding: 40px;
  font-size: 1.1em;
  color: #888e95;
}

.upload-error-message {
  color: #ff5555;
  background-color: rgba(255, 85, 85, 0.1);
  border: 1px solid rgba(255, 85, 85, 0.3);
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 0.9em;
}

.banner-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
}

.banner-card {
  background-color: rgba(35, 35, 45, 0.7);
  border: 1px solid rgba(160, 100, 220, 0.3);
  border-radius: 8px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 15px rgba(0,0,0,0.3);
  overflow: hidden;
}

.banner-image-container {
  width: 100%;
  aspect-ratio: 16 / 7;
  background-color: rgba(50, 50, 60, 0.8);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  overflow: hidden;
  position: relative; /* For status badge */
}

.banner-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.status-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75em;
  font-weight: bold;
  color: #fff;
  text-shadow: 0 0 3px rgba(0,0,0,0.5);
}
.disabled-badge {
  background-color: rgba(108, 117, 125, 0.8); /*灰色表示禁用*/
}


.banner-info {
  margin-bottom: 10px;
  flex-grow: 1;
}
.banner-title {
  font-size: 1.1em;
  color: #e0e0e0;
  margin-top: 0;
  margin-bottom: 5px;
  font-weight: 600;
  word-break: break-word;
}
.banner-detail {
  font-size: 0.85em;
  color: #a0a0b0;
  margin-bottom: 3px;
}
.banner-link {
  font-size: 0.85em;
  color: #a0a0b0;
  margin-bottom: 0;
  word-break: break-all;
}
.banner-link a {
  color: #8be9fd;
  text-decoration: none;
}
.banner-link a:hover {
  text-decoration: underline;
  color: #a0eefe;
}

.banner-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(160, 100, 220, 0.2);
}

.action-btn {
  background: none;
  border: 1px solid transparent;
  color: #adb5bd;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9em;
  display: inline-flex;
  align-items: center;
}
.action-btn i {
  margin-right: 6px;
}
.action-btn.edit-btn {
  border-color: rgba(139, 233, 253, 0.5);
  color: #8be9fd;
}
.action-btn.edit-btn:hover {
  background-color: rgba(139, 233, 253, 0.15);
  color: #a0eefe;
  border-color: rgba(139, 233, 253, 0.8);
}
.action-btn.delete-btn {
  border-color: rgba(255, 85, 85, 0.5);
  color: #ff8080;
}
.action-btn.delete-btn:hover {
  background-color: rgba(255, 85, 85, 0.15);
  color: #ff6666;
  border-color: rgba(255, 85, 85, 0.8);
}

/* Modal Styles */
.modal-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0,0,0,0.75); display: flex;
  align-items: center; justify-content: center; z-index: 1000;
  overflow-y: auto; padding: 20px; box-sizing: border-box;
}
.modal-content {
  background-color: rgba(30, 30, 40, 0.98); padding: 30px 40px;
  border-radius: 8px; border: 1px solid rgba(160, 100, 220, 0.6);
  box-shadow: 0 8px 30px rgba(0,0,0,0.6); width: 100%;
  max-width: 600px; color: #e0e0e0; margin: auto;
}
.modal-title {
  color: #ff8c69; margin-top: 0; margin-bottom: 25px;
  font-size: 1.5em; text-align: center; font-weight: 600;
}
.form-group { margin-bottom: 20px; }
.form-group label {
  display: block; margin-bottom: 8px; font-size: 0.9em; color: #a0a0b0;
}
.form-input, .form-input-file {
  width: 100%; padding: 10px 12px;
  background-color: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(160, 100, 220, 0.4);
  border-radius: 5px; color: #e0e0e0; font-size: 1em; box-sizing: border-box;
}
.form-input-file { padding: 8px 12px; line-height: 1.5; }
.form-input:focus, .form-input-file:focus {
  outline: none; border-color: #ff8c69;
  box-shadow: 0 0 8px rgba(255, 140, 105, 0.3);
}
.image-preview-container-modal {
  margin-top: 10px; text-align: center;
  border: 1px dashed rgba(160, 100, 220, 0.3);
  padding: 10px; border-radius: 6px; min-height: 100px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
}
.image-preview-modal {
  max-width: 100%; max-height: 150px; /* 預覽圖小一點 */
  border-radius: 4px;
  display: block; margin-bottom: 10px;
}
.remove-image-btn {
  background: none; border: 1px solid #ff8080; color: #ff8080;
  padding: 5px 10px; border-radius: 4px; font-size: 0.8em;
  cursor: pointer; transition: all 0.2s ease; margin-top: 5px;
}
.remove-image-btn:hover {
  background-color: rgba(255, 85, 85, 0.15); color: #ff6666;
}
small.image-upload-hint {
  display: block; margin-top: 5px; font-size: 0.8em; color: #888e95;
}
.required-star { color: #ff5555; margin-left: 3px; }

.form-group.toggle-form-group {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 18px; padding: 3px 0;
}
.toggle-switch-label {
  font-size: 0.9em; color: #a0a0b0; margin-right: 15px;
  line-height: 28px; white-space: nowrap;
}
.switch {
  position: relative; display: inline-block; width: 50px; height: 28px; flex-shrink: 0;
}
.switch input { opacity: 0; width: 0; height: 0; }
.slider {
  position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(80, 80, 90, 0.7); transition: .3s;
  border: 1px solid rgba(120, 120, 130, 0.5);
}
.slider:before {
  position: absolute; content: ""; height: 20px; width: 20px;
  left: 3px; bottom: 3px; background-color: #e0e0e0; transition: .3s;
}
input:checked + .slider { /* 通用選中樣式，可以被特定 ID 覆蓋 */
  background-color: #50fa7b; border-color: #3ddc67;
}
#bannerEnabled:checked + .slider { /* enabled 的特定顏色 */
  background-color: #50fa7b; /* 綠色 */
  border-color: #3ddc67;
}
input:focus + .slider { box-shadow: 0 0 8px rgba(139, 233, 253, 0.4); }
#bannerEnabled:checked:focus + .slider { box-shadow: 0 0 8px rgba(80, 250, 123, 0.6); }

input:checked + .slider:before { transform: translateX(22px); background-color: #1a1a1a; }
.slider.round { border-radius: 28px; }
.slider.round:before { border-radius: 50%; }

.modal-actions { display: flex; justify-content: flex-end; gap: 15px; margin-top: 30px;}
.modal-btn {
  padding: 10px 20px; border: none; border-radius: 5px;
  font-size: 0.95em; font-weight: 500; cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;
}
.modal-btn.cancel-btn { background-color: rgba(80, 80, 90, 0.7); color: #c0c0c0; }
.modal-btn.cancel-btn:hover { background-color: rgba(90, 90, 100, 0.8); }
.modal-btn.save-btn { background: linear-gradient(145deg, #50fa7b, #3ddc67); color: #1a1a1a; }
.modal-btn.save-btn:hover:not(:disabled) { background: linear-gradient(145deg, #60fc8b, #4dfd77); transform: translateY(-1px); }
.modal-btn:disabled { opacity: 0.7; cursor: not-allowed; }
.modal-btn.save-btn i { margin-right: 5px; }

/* SweetAlert2 全局樣式 (如果您已在 ProductsView 中定義，這裡可以省略或確保一致) */
:global(.swal2-dark-popup) {
  background-color: rgba(35, 35, 45, 0.98) !important;
  border: 1px solid rgba(160, 100, 220, 0.6) !important;
  color: #e0e0e0 !important;
}
:global(.swal2-dark-title) {
  color: #ff8c69 !important;
}
:global(.swal2-dark-html-container) {
  color: #c0c0c0 !important;
}
:global(.swal2-dark-confirm-button) {
  background-color: #d33 !important; /* 紅色 */
  border-color: #d33 !important;
  color: #fff !important;
}
:global(.swal2-dark-confirm-button:hover) {
  background-color: #c82333 !important;
  border-color: #bd2130 !important;
}
:global(.swal2-dark-cancel-button) {
  background-color: rgba(80, 80, 90, 0.7) !important;
  color: #c0c0c0 !important;
}
:global(.swal2-dark-cancel-button:hover) {
  background-color: rgba(90, 90, 100, 0.8) !important;
}
</style>